// ============================================================================
// BOQ — Rate observations (the rate library / history)
// ============================================================================
// boq.rates holds empirical rate observations per item_version: quoted/billed
// per vendor / city / date, with provenance (source filename + consultant for
// imported sample BOQs). project_id is NULL for library/market observations
// (reusable across projects) and set for project-specific quotes.
//
// This hook loads observations for a single item_version (the rate history a
// line can be priced from) plus a small vendor lookup for the picker.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError, normalizeId } from '../../hooks/useSupabase';

const SELECT = `
  id, item_version_id, project_id, vendor_id, city, rate_date,
  rate_quoted, rate_billed, contractor, source, source_filename, source_consultant,
  created_at
`.replace(/\s+/g, ' ');

const num = (v) => (v === null || v === undefined ? null : Number(v));

const mapRate = (r) => ({
  id: r.id,
  itemVersionId: r.item_version_id,
  projectId: r.project_id,
  vendorId: normalizeId(r.vendor_id),
  city: r.city,
  rateDate: r.rate_date,
  rateQuoted: num(r.rate_quoted),
  rateBilled: num(r.rate_billed),
  contractor: r.contractor,
  source: r.source,
  sourceFilename: r.source_filename,
  sourceConsultant: r.source_consultant,
  createdAt: r.created_at,
});

export const useBoqRates = (itemVersionId) => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!itemVersionId) { setRates([]); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        .schema('boq')
        .from('rates')
        .select(SELECT)
        .eq('item_version_id', itemVersionId)
        .order('rate_date', { ascending: false })
        .order('created_at', { ascending: false });
      if (fetchErr) throw fetchErr;
      setRates((data || []).map(mapRate));
    } catch (err) {
      console.error('[useBoqRates] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'rates' }));
    } finally {
      setLoading(false);
    }
  }, [itemVersionId]);

  const createRate = useCallback(async (input) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to record a rate.' };
      }
      const quoted = (input.rateQuoted === '' || input.rateQuoted == null) ? null : Number(input.rateQuoted);
      const billed = (input.rateBilled === '' || input.rateBilled == null) ? null : Number(input.rateBilled);
      if (quoted == null && billed == null) {
        return { success: false, error: 'Enter a quoted or billed rate.' };
      }
      const payload = {
        item_version_id: itemVersionId,
        project_id: input.projectId || null,
        vendor_id: input.vendorId || null,
        city: input.city?.trim() || null,
        rate_date: input.rateDate || new Date().toISOString().slice(0, 10),
        rate_quoted: quoted,
        rate_billed: billed,
        contractor: input.contractor?.trim() || null,
        source: input.source || 'manual',
        source_filename: input.sourceFilename?.trim() || null,
        source_consultant: input.sourceConsultant?.trim() || null,
      };
      const { data, error: insErr } = await supabase
        .schema('boq').from('rates').insert(payload).select(SELECT).single();
      if (insErr) throw insErr;
      const mapped = mapRate(data);
      setRates((prev) => [mapped, ...prev]);
      return { success: true, rate: mapped };
    } catch (err) {
      console.error('[useBoqRates] create failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'rate' }) };
    }
  }, [itemVersionId]);

  const deleteRate = useCallback(async (id) => {
    try {
      const { error: delErr } = await supabase.schema('boq').from('rates').delete().eq('id', id);
      if (delErr) throw delErr;
      setRates((prev) => prev.filter((r) => r.id !== id));
      return { success: true };
    } catch (err) {
      console.error('[useBoqRates] delete failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'rate' }) };
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return { rates, loading, error, refresh: fetchAll, createRate, deleteRate };
};

// Lightweight vendor lookup for the rate picker — id + company_name only.
export const useVendorLookup = () => {
  const [vendors, setVendors] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('vendors')
          .select('id, company_name')
          .order('company_name');
        if (!error && !cancelled) {
          setVendors((data || []).map((v) => ({ id: normalizeId(v.id), name: v.company_name || '(unnamed)' })));
        }
      } catch (err) {
        console.error('[useVendorLookup] failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return vendors;
};
